import React, { useEffect } from 'react';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';

import { HitsPagination } from '@/components/HitsPagination';
import { FiltersApplied } from '@/components/ListingFilters/components/FiltersApplied/FiltersApplied';
import { FiltersButton } from '@/components/ListingFilters/components/FiltersButton';
import { FiltersModal } from '@/components/ListingFilters/components/FiltersModal';
import { ListingFiltersProvider } from '@/components/ListingFilters/ListingFiltersProvider';
import { ProductListingCard } from '@/components/ProductListingCard';
import { useFiltersModalScroll } from '@/components/RetailerWidget/components/RetailerProductList/useFiltersModalScroll';
import { Typography } from '@/components/Typography';
import { useIsMdDown } from '@/hooks/useMedia';
import { sendAlgoliaEvent } from '@/integrations/algolia/helpers';
import type { StrapHit, WatchHit } from '@/integrations/algolia/types';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { commonMessages } from '@/messages/common';
import { useAlgoliaEvent } from '@/providers/AlgoliaEventProvider';
import { RefNumberProvider } from '@/providers/RefNumberProvider/RefNumberProviderContext';
import { listingActions } from '@/states/listingState';

import * as S from './RetailerProductList.styles';

interface RetailerProductListProps {
  onProductSelect: (product: WatchHit | StrapHit) => void;
}

export const RetailerProductList: React.FC<RetailerProductListProps> = ({
  onProductSelect,
}) => {
  const { hits, results } = useHits<WatchHit | StrapHit>();
  const { setIndexName, setIsSearched, setQueryID } = useAlgoliaEvent();
  const { status } = useInstantSearch();
  const isMobile = useIsMdDown();
  const queryID = results?.queryID ?? '';
  const noResults =
    !results?.nbHits && status === 'idle' && results && results.queryID;

  useFiltersModalScroll('filters-modal-body');

  useEffect(() => {
    listingActions.updateListingType('WATCH');

    if (hits.length > 0) {
      setIsSearched(true);
      setQueryID(queryID);
      setIndexName(results?.index ?? '');
    }
  }, [hits, queryID, results?.index, setIndexName, setIsSearched, setQueryID]);

  const handleProductClick = (
    e: React.MouseEvent,
    hit: WatchHit | StrapHit,
    index: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    sendAlgoliaEvent({
      event: AlgoliaEventsNames.clickedObjectIDsAfterSearch,
      objectIDs: [hit.objectID],
      queryID: queryID,
      indexName: results?.index,
      positions: [index + 1],
    });

    onProductSelect(hit);
    return false;
  };

  return (
    <ListingFiltersProvider listingFiltersType='PLP'>
      <RefNumberProvider>
        <S.ProductListContainer>
          <S.FilterContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FiltersButton isWidget={true} />
              {!isMobile && <FiltersApplied />}
            </div>

            <S.ResultCount>
              {results?.nbHits || 0}{' '}
              <FormattedMessage {...commonMessages.watchesFound} />
            </S.ResultCount>
          </S.FilterContainer>
          {isMobile && <FiltersApplied />}

          <FiltersModal
            collectionData={[]}
            isWidget={true}
            showCollectionFilter={true}
            showMaterialsFilter={true}
          />

          {noResults ? (
            <S.EmptyResults>
              <Typography
                data-test-id='RetailerProductListNoResultsFound'
                fontFamily='italianPlate'
                m={6}
                scale='h3'
                textAlign='center'
              >
                <FormattedMessage {...commonMessages.noResultsFound} />
              </Typography>
            </S.EmptyResults>
          ) : (
            <S.ProductsGrid data-test-id='RetailerProductListGrid'>
              {hits?.map((hit, index) => (
                <S.ProductCardWrapper
                  key={hit.objectID}
                  data-test-id={`RetailerProductListCard-${index}`}
                  onClick={(e) => handleProductClick(e, hit, index)}
                >
                  <ProductListingCard
                    hideFavorite={true}
                    hit={hit}
                    isMostRelevantCta={false}
                    productCatalogMode={true}
                    productCatalogModeCtaStyle='DISCOVER'
                  />
                </S.ProductCardWrapper>
              ))}
            </S.ProductsGrid>
          )}

          <S.PaginationContainer>
            <HitsPagination />
          </S.PaginationContainer>
        </S.ProductListContainer>
      </RefNumberProvider>
    </ListingFiltersProvider>
  );
};
